import { CircleAlertIcon, RefreshCcw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { useGetPageTextContent } from "~/api/documents/documents";
import { useGetKnowledgebasePageContent } from "~/api/knowledge/knowledge";
import type { Language, ReportSection } from "~/api/model";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useDocList } from "~/features/chat/hooks/useDocList";
import { useChatDocumentParams } from "~/helpers";
import {
  TERMS_PROSE_CLASS,
  getTermsMarkdownComponents,
  highlightTermsInMarkdown,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";
import { Hint } from "../../types";
import { ReportSectionView } from "./ReportSection";
import { SourceReference } from "./ReportSectionSources";
import { OutdatedReason } from "./TemplateReportSelector";

export type ReportViewStatus =
  | { type: "loading" }
  | { type: "pending" }
  | { type: "ready" }
  | { type: "outdated"; reason?: OutdatedReason }
  | { type: "templateDeleted" };

type ReportViewProps = {
  reportSections: ReportSection[];
  status: ReportViewStatus;
  terms: Hint[];
  content: string;
  onRegenerateSection: (sectionId: string) => void;
  onEditSection: (sectionId: string, content: string) => Promise<void>;
  onRegenerateReport?: () => void;
  isRegeneratingReport?: boolean;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionsAvailable?: boolean;
};

export const ReportView = ({
  reportSections,
  status,
  terms,
  content,
  onRegenerateSection,
  onEditSection,
  onRegenerateReport,
  isRegeneratingReport,
  templateName,
  outputLanguage,
  isActionsAvailable = true,
}: ReportViewProps) => {
  const { formatMessage } = useIntl();
  const { organizationId, caseId } = useChatDocumentParams();
  const { docList, pageItems, absolutePageNumbers } = useDocList();

  // Source modal state
  const [pageTextModalOpen, setPageTextModalOpen] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [pageImageUrl, setPageImageUrl] = useState<string | undefined>(
    undefined
  );
  const [pageRotation, setPageRotation] = useState(0);

  // Knowledge base source state
  const [selectedKbDocumentId, setSelectedKbDocumentId] = useState<
    string | null
  >(null);
  const [selectedKbPageNumber, setSelectedKbPageNumber] = useState<
    number | null
  >(null);
  const [isKnowledgeBaseSource, setIsKnowledgeBaseSource] = useState(false);

  // Fetch case document page content
  const {
    data: pageContentData,
    isLoading: isPageContentLoading,
    error: pageContentError,
  } = useGetPageTextContent(organizationId, caseId, selectedPageId ?? "", {
    query: {
      enabled:
        !isKnowledgeBaseSource &&
        !!selectedPageId &&
        !!caseId &&
        selectedPageId !== "unknown" &&
        organizationId !== "unknown",
    },
  });

  // Fetch knowledge base document page content
  const {
    data: kbPageContentData,
    isLoading: isKbPageContentLoading,
    error: kbPageContentError,
  } = useGetKnowledgebasePageContent(
    organizationId,
    selectedKbDocumentId ?? "",
    selectedKbPageNumber ?? 0,
    {
      query: {
        enabled:
          isKnowledgeBaseSource &&
          !!selectedKbDocumentId &&
          !!selectedKbPageNumber &&
          selectedKbPageNumber > 0 &&
          organizationId !== "unknown",
      },
    }
  );

  const handleSourceClick = useCallback(
    async (source: SourceReference) => {
      const isKbSource = source.isKnowledgeBase === true;

      if (isKbSource) {
        // Knowledge base source - pageId is the page number, documentId is the knowledgeDocumentId
        const pageNumber = parseInt(source.pageId, 10);
        if (!isNaN(pageNumber) && source.documentId) {
          setIsKnowledgeBaseSource(true);
          setSelectedKbDocumentId(source.documentId);
          setSelectedKbPageNumber(pageNumber);
          setSelectedPageId(null);
          setPageImageUrl(undefined);
          setPageRotation(0);
          setIsContentLoading(false);
          setPageTextModalOpen(true);
        }
      } else {
        // Case document source - pageId is the page UUID
        setIsKnowledgeBaseSource(false);
        setSelectedKbDocumentId(null);
        setSelectedKbPageNumber(null);
        setSelectedPageId(source.pageId);
        setIsContentLoading(true);
        setPageTextModalOpen(true);

        const pageItem = pageItems.find((p) => p.pageId === source.pageId);
        if (pageItem) {
          setPageRotation(pageItem.rotate);

          try {
            const urls = await pageItem.getUrls();
            setPageImageUrl(urls.scanUrl || urls.highResUrl || undefined);
          } catch {
            setPageImageUrl(undefined);
          } finally {
            setIsContentLoading(false);
          }
        } else {
          setIsContentLoading(false);
        }
      }
    },
    [pageItems]
  );

  const modalTitle = useMemo(() => {
    if (isKnowledgeBaseSource) {
      return `${kbPageContentData?.filename ?? "Knowledge Base Document"} - Page ${selectedKbPageNumber}`;
    }

    if (!selectedPageId) return "Page Content Recognition";

    const pageItem = pageItems.find((p) => p.pageId === selectedPageId);
    if (!pageItem) return "Page Content Recognition";

    const section = docList?.tree
      .flatMap((doc) => doc.sections)
      .find((sec) => sec.id === pageItem.sectionId);

    const sectionName = section?.name || "Unknown Section";
    const pageNumber = absolutePageNumbers[selectedPageId] || 0;

    return `${sectionName} – Page ${pageNumber}`;
  }, [
    isKnowledgeBaseSource,
    kbPageContentData?.filename,
    selectedKbPageNumber,
    selectedPageId,
    pageItems,
    docList,
    absolutePageNumbers,
  ]);

  const highlightedContent = useMemo(() => {
    if (!content) return "";
    const preprocessed = unwrapAllCodeBlocks(content);
    return highlightTermsInMarkdown(preprocessed, terms);
  }, [content, terms]);

  if (status.type === "loading") {
    return (
      <div className="max-w-sm animate-pulse">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index}>
            {index % 2 === 0 ? (
              <Skeleton className="h-4 bg-muted rounded-full w-48 mb-4" />
            ) : (
              <Skeleton className="h-4 bg-muted rounded-full w-32 mb-4" />
            )}
            <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[330px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[300px] mb-2.5" />
            <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-6" />
          </div>
        ))}
      </div>
    );
  }

  if (reportSections.length > 0) {
    const getOutdatedMessage = () => {
      if (status.type === "outdated") {
        if (status.reason === "outdatedByDoclist") {
          return formatMessage({ id: "chat.report.outdatedByDoclistAlert" });
        }

        if (status.reason === "outdatedByTemplate") {
          return formatMessage({ id: "chat.report.outdatedByTemplateAlert" });
        }

        return formatMessage({ id: "chat.report.outdatedAlert" });
      }
      return "";
    };

    return (
      <div className="flex flex-col gap-4">
        {isActionsAvailable && status.type === "outdated" && (
          <Alert
            variant="default"
            className="border-amber-200 bg-amber-50 px-3 py-1.5"
          >
            <div className="flex items-center gap-2">
              <AlertDescription className="flex-1 text-amber-800 text-xs">
                {getOutdatedMessage()}
              </AlertDescription>
              {onRegenerateReport && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="shrink-0 text-amber-700 hover:bg-amber-100 hover:text-amber-900 gap-1"
                  onClick={onRegenerateReport}
                  disabled={isRegeneratingReport}
                >
                  <RefreshCcw className="h-3 w-3" />
                  {formatMessage({ id: "chat.report.rewriteNow" })}
                </Button>
              )}
            </div>
          </Alert>
        )}
        {isActionsAvailable && status.type === "templateDeleted" && (
          <Alert variant="default" className="border-border bg-muted">
            <AlertDescription className="flex items-center gap-2 text-foreground">
              <CircleAlertIcon className="h-4 w-4" />
              {formatMessage({ id: "chat.report.templateDeletedAlert" })}
            </AlertDescription>
          </Alert>
        )}
        {reportSections
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((section) => (
            <ReportSectionView
              key={section.id}
              reportSection={section}
              terms={terms}
              onRegenerate={() => onRegenerateSection(section.id ?? "")}
              onEdit={async (content: string) =>
                onEditSection(section.id ?? "", content)
              }
              isRegenerating={
                section.status === "processing" || section.status === "pending"
              }
              status={status}
              templateName={templateName}
              outputLanguage={outputLanguage}
              isActionAvailable={isActionsAvailable}
              onSourceClick={handleSourceClick}
            />
          ))}

        {pageTextModalOpen && (
          <MarkdownContentModal
            title={modalTitle}
            content={
              isKnowledgeBaseSource
                ? isKbPageContentLoading
                  ? "Loading content..."
                  : kbPageContentError
                    ? "Error loading content. This page may not have processed content available yet."
                    : kbPageContentData?.content ||
                      "No content available for this page."
                : isPageContentLoading || isContentLoading
                  ? "Loading content..."
                  : pageContentError
                    ? "Error loading content. This page may not have processed content available yet."
                    : pageContentData?.content ||
                      "No content available for this page."
            }
            filename={
              isKnowledgeBaseSource
                ? kbPageContentData?.filename
                : pageContentData?.filename
            }
            authorName={
              isKnowledgeBaseSource
                ? (kbPageContentData?.authorName ?? undefined)
                : pageContentData?.authorName
            }
            uploadDate={
              isKnowledgeBaseSource
                ? kbPageContentData?.uploadDate
                : pageContentData?.uploadDate
            }
            open={pageTextModalOpen}
            onOpenChange={(open) => {
              setPageTextModalOpen(open);
              if (!open) {
                // Reset state when modal closes
                setIsKnowledgeBaseSource(false);
                setSelectedKbDocumentId(null);
                setSelectedKbPageNumber(null);
                setSelectedPageId(null);
                setPageImageUrl(undefined);
                setPageRotation(0);
              }
            }}
            copyEventName="app/report/page_text_modal/copy_all.button/click"
            exportOpenEventName="app/report/page_text_modal/export.button/click"
            exportEventName="app/report/page_text_modal/export_menu_open/export_action.link/click"
            terms={terms}
            pageImageUrl={
              isKnowledgeBaseSource
                ? (kbPageContentData?.scanUrl ?? undefined)
                : pageImageUrl
            }
            pageRotation={isKnowledgeBaseSource ? 0 : pageRotation}
          />
        )}
      </div>
    );
  }

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={[rehypeRaw]}
      className={TERMS_PROSE_CLASS}
      components={{
        ...getTermsMarkdownComponents(terms),
        h1: (props) => (
          <h1 {...props} className="text-lg font-medium mb-2 normal-case" />
        ),
        h2: (props) => <h2 {...props} className="text-base font-bold mb-1" />,
        h3: (props) => (
          <h3 {...props} className="text-sm font-medium uppercase mb-1" />
        ),
      }}
    >
      {highlightedContent}
    </Markdown>
  );
};
